import { getStargazersData } from './firebaseStargazers';
import { processStargazersLocations, createGlobeArcs } from '../utils/locationUtils';

// Global data cache
class DataPreloader {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
    this.isPreloading = false;
  }

  // Preload stargazers data on app startup
  async preloadStargazersData() {
    const cacheKey = 'stargazers_globe_data';
    
    // Return existing data if already cached
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Return existing promise if already loading
    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey);
    }

    // Start loading
    this.isPreloading = true;
    const loadingPromise = this.loadStargazersData();
    this.loadingPromises.set(cacheKey, loadingPromise);

    try {
      const result = await loadingPromise;
      this.cache.set(cacheKey, result);
      this.loadingPromises.delete(cacheKey);
      this.isPreloading = false;
      
      return result;
    } catch (error) {
      this.loadingPromises.delete(cacheKey);
      this.isPreloading = false;
      throw error;
    }
  }

  // Internal method to load and process data
  async loadStargazersData() {
    try {
      // Fetch raw data
      const result = await getStargazersData();
      const data = result.stargazers;

      // Process the data
      const countries = processStargazersLocations(data);
      const arcs = createGlobeArcs(countries);

      return {
        globeData: arcs,
        totalStargazers: data.length,
        timestamp: Date.now(),
        processed: true
      };
    } catch (error) {
      throw new Error(`Failed to load stargazers data: ${error.message}`);
    }
  }

  // Get preloaded data (instant if available)
  getStargazersData() {
    const cacheKey = 'stargazers_globe_data';
    return this.cache.get(cacheKey) || null;
  }

  // Check if data is currently being preloaded
  isPreloadingStargazers() {
    return this.isPreloading;
  }

  // Get loading promise if data is being loaded
  getLoadingPromise() {
    const cacheKey = 'stargazers_globe_data';
    return this.loadingPromises.get(cacheKey) || null;
  }

  // Clear cache (useful for testing or forced refresh)
  clearCache() {
    this.cache.clear();
    this.loadingPromises.clear();
    this.isPreloading = false;
  }

  // Check if data is stale (older than 5 minutes)
  isDataStale() {
    const cacheKey = 'stargazers_globe_data';
    const data = this.cache.get(cacheKey);
    if (!data) return true;
    
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    return (Date.now() - data.timestamp) > CACHE_DURATION;
  }

  // Background refresh (non-blocking)
  async backgroundRefresh() {
    if (this.isPreloading) return;
    
    try {
      await this.preloadStargazersData();
    } catch (error) {
      // Silent background refresh failure
    }
  }
}

// Create singleton instance
const dataPreloader = new DataPreloader();

export default dataPreloader;

// Export convenience methods
export const preloadStargazersData = () => dataPreloader.preloadStargazersData();
export const getPreloadedStargazersData = () => dataPreloader.getStargazersData();
export const isPreloadingStargazers = () => dataPreloader.isPreloadingStargazers();
export const getStargazersLoadingPromise = () => dataPreloader.getLoadingPromise();
export const isStargazersDataStale = () => dataPreloader.isDataStale();
export const backgroundRefreshStargazers = () => dataPreloader.backgroundRefresh();